import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Menu, X, Globe } from 'lucide-react';
import Icon from '@mdi/react';
import { 
  routes, 
  CITIES, 
  cityIcons,
  getCityRoutes 
} from '../config/routes';

const Sidebar = ({ user }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setExpandedSections({});
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const renderNavLinks = () => (
    <div className="flex-none space-y-1">
      {Object.entries(routes.nav).map(([key, route]) => {
        if (route.type === 'auth' && !user) return null;
        if (key === 'auth' && user) return null;

        const Icon = route.icon;

        return (
          <Link 
            key={route.path}
            to={route.path} 
            className={`flex items-center ${
              isExpanded ? 'justify-start space-x-3' : 'justify-center'
            } p-3 rounded-lg hover:bg-gray-700 transition-colors`}
          >
            <Icon className="flex-shrink-0 w-5 h-5" />
            <span className={`${!isExpanded && 'hidden'} transition-opacity duration-300`}>
              {route.text}
            </span>
          </Link>
        );
      })}
    </div>
  );

  const renderCityLinks = (cityKey) => {
    const cityRoutes = getCityRoutes(cityKey);
    return Object.entries(cityRoutes).map(([key, route]) => (
      <Link 
        key={route.path}
        to={route.path} 
        className={`block pl-4 py-1 text-sm hover:text-white transition-colors ${
          route.type === 'premium' ? 'text-emerald-400' : 'text-gray-400'
        }`}
      >
        {route.text}
        {route.type === 'premium' && !user && (
          <span className="ml-2 text-xs bg-emerald-900 text-emerald-200 px-2 py-0.5 rounded">
            Expert
          </span>
        )}
        {route.type === 'public' && (
          <span className="ml-2 text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
            Free
          </span>
        )}
      </Link>
    ));
  };

  const renderCities = () => (
    <div className="space-y-1">
      {Object.keys(CITIES).map(cityKey => {
        const city = CITIES[cityKey];
        return (
          <div key={city} className="space-y-1">
            <div className="flex items-center">
              <Link 
                to={`/${city}`}
                className="flex items-center space-x-2 pl-4 py-1 text-gray-200 hover:text-white"
              >
                <Icon path={cityIcons[city]} size={0.8} />
                <span>
                  {city.charAt(0).toUpperCase() + city.slice(1)}
                </span>
              </Link>
              <button 
                onClick={() => toggleSection(city)}
                className="ml-auto p-1 hover:bg-gray-700 rounded"
              >
                <ChevronRight 
                  size={14} 
                  className={`transition-transform duration-200 ${
                    expandedSections[city] ? 'rotate-90' : ''
                  }`}
                />
              </button>
            </div>
            {expandedSections[city] && (
              <div className="ml-4 space-y-1">
                {renderCityLinks(city)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-4 left-4 z-40 bg-gray-800 p-2 rounded-lg text-white"
        >
          {isExpanded ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      <div 
        className={`fixed top-0 left-0 h-screen bg-gray-800 text-white p-4 transition-all duration-300 ease-in-out z-40
          ${isExpanded ? 'w-72' : 'w-16'}
          ${isMobile && !isExpanded ? '-translate-x-full' : 'translate-x-0'}
        `}
        onMouseEnter={() => !isMobile && setIsExpanded(true)}
        onMouseLeave={() => !isMobile && setIsExpanded(false)}
      >
        <div className="flex flex-col h-full space-y-4">
          {renderNavLinks()}
          <div className="flex-1 space-y-2 overflow-y-auto">
            {renderCities()}
          </div>
        </div>
      </div>

      {isMobile && isExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

export default Sidebar;